import type { Locale } from '@autho/i18n';
import { isLocale } from '@autho/i18n';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ApiRequestError } from '../lib/api.ts';
import { useOnline } from '../lib/online.ts';
import { useT } from '../lib/preferences.tsx';
import { TROUBLE_MESSAGE, speaksForItself, troubleOf } from '../lib/trouble.ts';

export function useLocale(): Locale {
  const { locale } = useIntl();
  return isLocale(locale) ? locale : 'en';
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border px-6 py-4">
      <div>
        <h1 className="text-title">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="px-6 py-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-section">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Loading() {
  const t = useT();
  return (
    <p role="status" className="px-6 py-4 text-sm text-muted">
      {t('app.loading')}
    </p>
  );
}

/**
 * Nothing silent (Design Principles #8): show what failed, in words.
 *
 * The server's own sentence when it sent one and it is about this request — a 4xx is written for the
 * person. Otherwise the trouble decides, so *offline*, *the server is not answering* and *your session
 * expired* stay three different things here as they are on the gate.
 */
export function ErrorNote({ error }: { error: unknown }) {
  const t = useT();
  const online = useOnline();
  const trouble = troubleOf(error, online);
  // `stated` and `unknown` both mean *the answer said it better than this table can*. For `stated` the
  // server chose a code and wrote a sentence; showing the table's line instead is how the estimate's real
  // cause stayed hidden behind *it has been logged* (F208).
  const message =
    speaksForItself(trouble) && error instanceof ApiRequestError
      ? error.message
      : t(TROUBLE_MESSAGE[trouble]);
  return (
    <p role="alert" className="px-6 py-4 text-sm text-danger">
      {message}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-3 text-sm text-muted">{children}</p>;
}
